import { sourceControlToggleAtom, toggleLocalGuidelinesAtom, toggleMyLibraryAtom, togglePrimaryEvidenceAtom, toggleWebSearchAtom } from '@/atoms/sourceControlAtoms';
import { toast } from '@heidi/design-system/toast';
import { resolveCheckOtherSourcesUpdates } from '@/components/chat/utils/checkOtherSources';
import { useHasPermissionToReplace } from '@/components/ui/ask-ai-input/hooks/useHasPermissionToReplace';
import { useEvidenceCopyConfirmationGate } from '@/hooks/useEvidenceCopyConfirmationGate';
import { useTeamEvidenceEnforcement } from '@heidi/evidence';
import { logger } from '@/utils/logger/logger';
import { useTranslations } from '@heidi/runtime/i18n';
import type { MessageEntryMethod } from '@heidi/evidence/contracts';
import { useAtomValue, useSetAtom } from 'jotai';
import { isEmpty } from 'lodash-es';
import { useCallback, useRef, type JSX } from 'react';
import { useChatResponseActions } from '../hooks/useChatResponseActions';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { extractMessageId } from '@heidi/evidence/contracts';
import type { ChatMode, SessionConversation } from '@heidi/evidence/contracts';
import { ActionRowButtons } from './ActionButtons';
import { CustomerFeedback } from './customer-feedback';
import { FollowUpChips } from './FollowUpChips';
import { RegenerateDropdown, type SourceType } from './RegenerateDropdown';

interface ReplyActionsProps {
  sessionId: string | null;
  conversation: SessionConversation;
  position: number;
  mode: ChatMode;
  actionableContent: string;
  /** Callback for sharing the chat */
  onShare?: () => void;
  /** Chat ID for follow-up questions */
  chatId?: string;
  /** Chat title for document naming when creating new tabs */
  chatTitle?: string;
  /** User's specialty for more relevant follow-up suggestions */
  specialty?: string;
  /** Whether the response is still streaming */
  isStreaming?: boolean;
  /** Callback when a follow-up question is selected */
  onSelectFollowUp?: (question: string, entryMethod?: MessageEntryMethod) => void;
  /** Tool calls made during this response */
  toolCalls?: string[];
  /** Whether this is the last text response in the chat */
  isLastTextResponse?: boolean;
  /** EVI-608: Hide action buttons (Copy, Insert) when user notes are present */
  hideActionButtons?: boolean;
}

export const ReplyActions = ({
  sessionId,
  conversation,
  position,
  mode,
  actionableContent,
  onShare,
  chatId,
  chatTitle,
  specialty,
  isStreaming = false,
  onSelectFollowUp,
  toolCalls,
  isLastTextResponse = false,
  hideActionButtons = false,
}: ReplyActionsProps): JSX.Element => {
  const { t } = useTranslations();
  const { trackCopied, trackCheckOtherSourcesClicked } = useEvidenceAnalytics();

  // Backend message ID of this response group — joins follow-up engagement to
  // the producing model (same key as evidence_source_clicked).
  const messageId = extractMessageId(conversation, position);

  const { onAddToNote, onReplaceNote, onAddNewTab } = useChatResponseActions(
    actionableContent,
    mode,
    sessionId,
    chatTitle,
    chatId,
    messageId
  );

  // EVI: Note-mutating actions (Add to note / Replace note / Create document)
  // must be hidden when the user lacks edit access to the session — e.g. an
  // admin reviewing another clinician's session without session-sharing edit
  // rights. Mirrors the gating already applied by ActionsPopover.
  const { hasPermissionToReplace } = useHasPermissionToReplace();

  // Source control state for regenerate dropdown
  const sourceControlState = useAtomValue(sourceControlToggleAtom);
  const setLocalGuidelines = useSetAtom(toggleLocalGuidelinesAtom);
  const setPrimaryEvidence = useSetAtom(togglePrimaryEvidenceAtom);
  const setMyLibrary = useSetAtom(toggleMyLibraryAtom);
  const setWebSearch = useSetAtom(toggleWebSearchAtom);
  const { sourceControl } = useTeamEvidenceEnforcement();

  // Static label for local guidelines display
  const regionLabel = 'Local';

  // Get the last user message for this response group
  // Used for both follow-up context and "check other sources" functionality
  const lastUserMessage = conversation.timeline
    .filter((entry) => entry.type === 'user_message')
    .pop();
  const userQuery =
    lastUserMessage?.type === 'user_message' ? lastUserMessage.content : '';

  // Handler for checking other sources - sends the same query with different source filters.
  // The toggle atoms are updated synchronously by Jotai, and useChatStream reads the
  // LATEST values from the store at stream-creation time via store.get(), so there is
  // no need for a setTimeout — the stale closure on onSelectFollowUp is irrelevant
  // because startStreaming reads fresh filters directly from the Jotai store.
  const handleCheckOtherSources = useCallback(
    (source: SourceType) => {
      if (!userQuery || !onSelectFollowUp) {
        return;
      }

      const updates = resolveCheckOtherSourcesUpdates(source, sourceControl);
      if (!updates) {
        return;
      }

      const apply = (want: boolean) => {
        if (updates.localGuidelines === want) setLocalGuidelines(want);
        if (updates.primaryEvidence === want) setPrimaryEvidence(want);
        if (updates.myLibrary === want) setMyLibrary(want);
        if (updates.webSearch === want) setWebSearch(want);
      };
      apply(true);
      apply(false);

      // Attribute the re-run to the "Check other sources" control rather than a
      // follow-up chip, so evidence_chat_submitted logs entry_method accordingly.
      onSelectFollowUp(userQuery, 'check_other_sources');
    },
    [
      userQuery,
      onSelectFollowUp,
      sourceControl,
      setLocalGuidelines,
      setPrimaryEvidence,
      setMyLibrary,
      setWebSearch,
    ]
  );

  // Fired when the "Check other sources" dropdown is opened.
  const handleCheckOtherSourcesOpen = useCallback(() => {
    trackCheckOtherSourcesClicked({ chatId, messageId, sessionId });
  }, [trackCheckOtherSourcesClicked, chatId, messageId, sessionId]);

  // Anchor for the Evidence copy PHI/PII confirmation popover (EVI-1452 / SWR-88).
  // The gate writes this element into the global popover atom so the singleton
  // popover positions next to whichever reply row triggered it.
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const { doCopyWithConfirmation } = useEvidenceCopyConfirmationGate({
    chatId,
    messageId,
    surface: 'reply_actions',
    anchorRef: copyButtonRef,
  });

  const runCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(actionableContent);
    } catch (error) {
      logger.error('Failed to copy chat message to clipboard', {
        error,
        messageLength: actionableContent?.length,
      });
      toast({
        tone: 'destructive',
        title: t('common.error'),
        description: t('chat.failedToCopy'),
      });
      return false;
    }
    try {
      trackCopied({
        surface: 'reply_actions',
        chatId,
        messageId,
        sessionId,
        mode,
      });
    } catch (error) {
      logger.error('Failed to track evidence_copied', { error, surface: 'reply_actions' });
    }
    // Success is confirmed by the CopyButton's own icon/tooltip swap, so a
    // tracker throw must not downgrade it to a failure.
    return true;
  }, [actionableContent, t, trackCopied, chatId, messageId, sessionId, mode]);

  const handleCopy = useCallback(
    () => doCopyWithConfirmation(runCopy),
    [doCopyWithConfirmation, runCopy]
  );

  // EVI-1495 / SWR-45: Allow users to retrieve the Evidence Chat ID for issue
  // investigations on platforms (Desktop / Mobile) where the URL is not visible.
  const handleCopyChatId = async () => {
    if (!chatId) return;
    try {
      await navigator.clipboard.writeText(chatId);
      toast({ tone: 'success', title: t('chat.chatIdCopied') });
    } catch (error) {
      logger.error('Failed to copy chat ID to clipboard', { error, chatId });
      toast({
        tone: 'destructive',
        title: t('common.error'),
        description: t('chat.failedToCopy'),
      });
    }
  };

  // TODO: Restore when Save Snippet is ready
  // const handleSaveSnippet = () => {
  //   toast({ title: 'Save snippet coming soon' });
  // };

  // TODO: Restore when Draft is ready
  // const handleDraft = () => {
  //   toast({ title: 'Draft document coming soon' });
  // };

  const hasSessionActions = !isEmpty(sessionId);
  // Only expose the note-mutating actions when the session exists AND the user
  // is allowed to edit it.
  const canEditSessionNote = hasSessionActions && hasPermissionToReplace;

  return (
    <div className="mt-2 flex flex-col gap-2">
      {/* Action buttons row */}
      <div className="flex flex-row items-center justify-between gap-2">
        {/* LEFT: Feedback + Check Other Sources */}
        <div className="flex min-w-0 flex-1 flex-row items-center gap-0.5">
          <CustomerFeedback
            className="shrink-0"
            conversation={conversation}
            position={position}
            mode={mode}
          />

          {/* Check Other Sources Dropdown - sends same query with different source filters */}
          {onSelectFollowUp && userQuery && (
            <div className="flex min-w-0 flex-1 items-center justify-start">
              <RegenerateDropdown
                sourcesUsed={sourceControlState}
                regionLabel={regionLabel}
                onRegenerateWithSource={handleCheckOtherSources}
                onOpen={handleCheckOtherSourcesOpen}
                sourceControl={sourceControl}
              />
            </div>
          )}
        </div>

        {/* RIGHT: Actions (max 5 including more button) */}
        {/* EVI-608: Hide action buttons when user notes are present (shown in UserNotesBlock) */}
        {!hideActionButtons && (
          <ActionRowButtons
            onCopy={handleCopy}
            onShare={onShare}
            onAddToNote={canEditSessionNote ? onAddToNote : undefined}
            onReplaceNote={canEditSessionNote ? onReplaceNote : undefined}
            onCreateNewTab={canEditSessionNote ? onAddNewTab : undefined}
            onCopyChatId={chatId ? () => void handleCopyChatId() : undefined}
            // onDraft={handleDraft} // TODO: Restore when Draft is ready
            // onSaveSnippet={handleSaveSnippet} // TODO: Restore when Save Snippet is ready
            hasSessionActions={canEditSessionNote}
            copyButtonRef={copyButtonRef}
          />
        )}
      </div>

      {/* Follow-up question chips - only shown for the last text response */}
      {onSelectFollowUp && chatId && (
        <FollowUpChips
          chatId={chatId}
          messageId={messageId}
          sessionId={sessionId ?? undefined}
          userQuery={userQuery}
          assistantResponse={actionableContent}
          toolCalls={toolCalls}
          specialty={specialty}
          isStreaming={isStreaming}
          onSelectQuestion={onSelectFollowUp}
          isLastResponse={isLastTextResponse}
          compact={!!sessionId}
        />
      )}
    </div>
  );
};
